import React, { useState } from 'react';
import { Table, Card, Typography, Button, Modal, Form, Input, Select, Space, Popconfirm, message, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { authApi } from '../api';
import { User, UserCreateRequest, UserEditRequest } from '../types';

const { Title } = Typography;
const { Option } = Select;

const Users: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  // 获取用户列表
  const { data: users, isLoading } = useQuery(
    'users',
    () => authApi.getUsers(),
  );

  // 获取分类列表（用于设置允许的分类）
  const { data: categories } = useQuery(
    'categories',
    () => authApi.getCategories(),
  );

  // 创建用户
  const createMutation = useMutation(
    (data: UserCreateRequest) => authApi.createUser(data),
    {
      onSuccess: () => {
        message.success('用户创建成功');
        queryClient.invalidateQueries('users');
        setIsModalVisible(false);
        form.resetFields();
      },
      onError: (error: any) => {
        const errorMessage = error.response?.data?.detail || '用户创建失败';
        message.error(errorMessage);
      },
    }
  );

  // 更新用户
  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: UserEditRequest }) => authApi.updateUser(id, data),
    {
      onSuccess: () => {
        message.success('用户更新成功');
        queryClient.invalidateQueries('users');
        setIsModalVisible(false);
        setEditingUser(null);
        form.resetFields();
      },
      onError: (error: any) => {
        const errorMessage = error.response?.data?.detail || '用户更新失败';
        message.error(errorMessage);
      },
    }
  );

  // 删除用户
  const deleteMutation = useMutation(
    (id: string) => authApi.deleteUser(id),
    {
      onSuccess: () => {
        message.success('用户删除成功');
        queryClient.invalidateQueries('users');
      },
      onError: (error: any) => {
        const errorMessage = error.response?.data?.detail || '用户删除失败';
        message.error(errorMessage);
      },
    }
  );

  const handleAdd = () => {
    setEditingUser(null);
    setIsModalVisible(true);
    form.resetFields();
  };

  const handleEdit = (record: User) => {
    setEditingUser(record);
    setIsModalVisible(true);
    form.setFieldsValue({
      allowed_categories: record.allowed_categories,
    });
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const handleSubmit = (values: UserCreateRequest | UserEditRequest) => {
    if (editingUser) {
      // 编辑用户时，只更新密码和允许的分类
      const editData: UserEditRequest = {};
      if (values.password) {
        editData.password = values.password;
      }
      if (values.allowed_categories !== undefined) {
        editData.allowed_categories = values.allowed_categories;
      }
      updateMutation.mutate({ id: editingUser.id, data: editData });
    } else {
      // 创建用户
      createMutation.mutate(values as UserCreateRequest);
    }
  };

  const columns = [
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      render: (username: string) => (
        <Space>
          <UserOutlined />
          {username}
        </Space>
      ),
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => (
        <Tag color={role === 'admin' ? 'red' : 'blue'}>
          {role === 'admin' ? '管理员' : '普通用户'}
        </Tag>
      ),
    },
    {
      title: '允许的分类',
      dataIndex: 'allowed_categories',
      key: 'allowed_categories',
      render: (categories: string[]) => (
        <Space wrap>
          {categories.length > 0 ? (
            categories.map((category) => (
              <Tag key={category} color="green">
                {category}
              </Tag>
            ))
          ) : (
            <span style={{ color: '#999' }}>无限制</span>
          )}
        </Space>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (time: string) => new Date(time).toLocaleDateString(),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: User) => (
        <Space>
          <Button 
            type="link" 
            icon={<EditOutlined />} 
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除这个用户吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button 
              type="link" 
              danger 
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2}>用户管理</Title>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={handleAdd}
        >
          添加用户
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={users?.data}
          loading={isLoading}
          rowKey="id"
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
        />
      </Card>

      <Modal
        title={editingUser ? '编辑用户' : '添加用户'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingUser(null);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {!editingUser && (
            <>
              <Form.Item
                name="username"
                label="用户名"
                rules={[{ required: true, message: '请输入用户名' }]}
              >
                <Input placeholder="请输入用户名" />
              </Form.Item>

              <Form.Item
                name="role"
                label="角色"
                rules={[{ required: true, message: '请选择角色' }]}
              >
                <Select placeholder="请选择角色">
                  <Option value="admin">管理员</Option>
                  <Option value="normal">普通用户</Option>
                </Select>
              </Form.Item>
            </>
          )}

          <Form.Item
            name="password"
            label="密码"
            rules={editingUser ? [] : [{ required: true, message: '请输入密码' }]}
          >
            <Input.Password 
              placeholder={editingUser ? "留空则不修改密码" : "请输入密码"} 
            />
          </Form.Item>

          <Form.Item
            name="allowed_categories"
            label="允许的分类"
            tooltip="普通用户只能查看指定分类的内容，管理员可以查看所有分类"
          >
            <Select 
              mode="multiple"
              placeholder="请选择允许的分类（可选）"
              allowClear
            >
              {categories?.data.map((category: string) => (
                <Option key={category} value={category}>{category}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={createMutation.isLoading || updateMutation.isLoading}
              >
                {editingUser ? '更新' : '创建'}
              </Button>
              <Button onClick={() => setIsModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Users;
